import { Paper, Typography, Box, Link } from '@mui/material'
import css from './styles.module.css'
import LoadSafe from './LoadSafe'
import { useCurrentChain } from '@/hooks/useChains'
import { useAppSelector } from '@/store'
import { selectRpc } from '@/store/settingsSlice'
import { selectNotifications } from '@/store/notificationsSlice'
import LoadRPCUrl from '@/components/welcome/WelcomeLogin/LoadRPCUrl'
import { resolveRpcUrl } from '@/config/lumiaRpc'
import { useEffect, useState } from 'react'
import { useWeb3 } from '@/hooks/wallets/web3'
import useWallet from '@/hooks/wallets/useWallet'
import { RPC_URL_ERROR_KEY } from '@/hooks/wallets/useInitWeb3'
import { CHAINLIST_URL } from '@/config/constants'
import NewSafe from './NewSafe'

const WelcomeLogin = () => {
  const web3 = useWeb3()
  const wallet = useWallet()
  const chain = useCurrentChain()
  const customRpc = useAppSelector(selectRpc)
  const customRpcUrl = resolveRpcUrl(chain, customRpc)
  const notifications = useAppSelector(selectNotifications)

  // resolveRpcUrl can still resolve customRpcUrl through the chain's own publicRpcUri after a
  // connection failure, since useInitWeb3 only clears the user's stored override, not the
  // fallback. Without this check the welcome screen would loop: fail, redirect here, fall back to
  // the same dead URL, look "connected" again. Scoped to "no wallet", since a connected wallet on
  // the wrong network raises the same notification group for an unrelated reason that this screen
  // already surfaces its own way.
  const hasUnreachableRpcError =
    !wallet && notifications.some((n) => n.groupKey === RPC_URL_ERROR_KEY && !n.isDismissed)

  const [forceShowRpcInput, setForceShowRpcInput] = useState(false)

  const toggleShowRpcInput = () => {
    setForceShowRpcInput(!forceShowRpcInput)
  }

  useEffect(() => {
    setForceShowRpcInput(false)
  }, [chain, web3])

  const hasConnection = Boolean(web3 || customRpcUrl)

  return (
    <Paper className={css.loginCard} data-testid="welcome-login">
      <Box className={css.loginContent}>
        <Typography variant="h3" mt="auto" pt={5} fontWeight={700}>
          Eternal Safe
        </Typography>
        {/* TODO(eternalsafe): Gracefully handle the web3 variable loading in after sometime */}
        {hasConnection && !forceShowRpcInput && !hasUnreachableRpcError ? (
          <>
            <Typography mb={2} textAlign="center">
              Create a new Safe or load an existing one.
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <LoadSafe />
              <NewSafe />
            </Box>
            {/* TODO(eternalsafe): Allow import of data here */}
          </>
        ) : chain ? (
          <>
            {hasUnreachableRpcError ? (
              <Typography textAlign="center" mb={2}>
                The default RPC endpoint for {chain.chainName} is not responding right now. You can find a public RPC
                URL on{' '}
                <Link href={`${CHAINLIST_URL}chain/${chain.chainId}`} color="primary" target="_blank" rel="noreferrer">
                  Chainlist
                </Link>
                , provide it below, or try connecting a wallet instead.
              </Typography>
            ) : hasConnection ? (
              <Typography textAlign="center" mb={2}>
                Set a different RPC endpoint for {chain.chainName} below.
              </Typography>
            ) : (
              <>
                <Typography textAlign="center">
                  Connecting a wallet on {chain.chainName} is enough to get started.
                </Typography>
                <Typography textAlign="center" mb={2}>
                  You can change the RPC endpoint in settings if you would like to use a different one.
                </Typography>
              </>
            )}
            <LoadRPCUrl hideRpcInput={() => setForceShowRpcInput(false)} />
          </>
        ) : (
          <Typography mb={2} textAlign="center">
            Please select a network from the dropdown above to get started.
          </Typography>
        )}

        <Typography variant="subtitle2" textAlign="center" mt="auto" pt={3}>
          {hasConnection && (
            <Link type="button" component="button" onClick={toggleShowRpcInput} color="primary">
              {forceShowRpcInput ? 'Close' : 'Open'} RPC URL input
            </Link>
          )}
        </Typography>
      </Box>
    </Paper>
  )
}

export default WelcomeLogin
